import { v4 } from 'uuid';
import {
  HasWorkoutLayer,
  Workout,
  WorkoutExercise,
  WorkoutSet,
  ExerciseMutationData,
  SetMutationData,
  Layer,
} from '@strength/domain-workout';
import { CompletionStatus, WorkoutLayerDomain } from '@strength/domain';
import { editExerciseEdits, editSetEdits } from './workout-draft';
import { getExercisesFromEntry } from '../../actuals/utils';

export function selectAlternateExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  alternateGroupId: string,
  selectedExerciseId: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const alternateGroup = workoutLayer?.layer?.alternateGroupEdits?.find(
    (g) => g.workoutAlternateGroupId === alternateGroupId,
  );

  return [
    {
      componentName: 'alternateGroupEdits',
      action: alternateGroup ? 'update' : 'create',
      data: {
        id: alternateGroup?.id ?? v4(),
        workoutAlternateGroupId: alternateGroupId,
        selectedExerciseId,
      },
    },
  ];
}

// handles updating the exercise status and also changes the set statuses if needed
// if an exercise is skipped, all its sets should be skipped
// if an exercise is completed, all sets should be completed unless they are skipped
export function editExerciseStatusEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutExerciseId: string,
  sets: WorkoutSet[],
  data: Partial<ExerciseMutationData>,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const edits: WorkoutLayerDomain.WorkoutLayerEdit[] = editExerciseEdits(
    workoutLayer,
    workoutExerciseId,
    data,
  );
  const newStatus = data.status;

  if (newStatus === 'skipped') {
    sets.forEach((s) =>
      edits.push(
        ...editSetEdits(workoutLayer, s.workoutSetId, {
          ...s,
          status: 'skipped',
        }),
      ),
    );
  } else if (newStatus === 'completed') {
    sets.forEach((s) =>
      s.status !== 'completed' && s.status !== 'skipped'
        ? edits.push(
            ...editSetEdits(workoutLayer, s.workoutSetId, {
              ...s,
              status: 'completed',
            }),
          )
        : null,
    );
  }

  return edits;
}

// handles updating the set status and also changes the exercise status if needed
// if all sets are skipped, the exercise should be skipped
// if all sets have a status it should be marked as completed
export function editSetStatusEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSetId: string,
  exercise: WorkoutExercise,
  setMutationData: SetMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const edits: WorkoutLayerDomain.WorkoutLayerEdit[] = editSetEdits(
    workoutLayer,
    workoutSetId,
    setMutationData,
  );

  const newStatus = setMutationData.status;
  // If no status change is provided, don't recompute exercise status
  if (typeof newStatus === 'undefined') {
    return edits;
  }

  const newSetStatuses =
    exercise?.sets?.map((s) =>
      s.workoutSetId === workoutSetId ? newStatus : s.status,
    ) || [];

  // Determine new exercise status based on set statuses
  let newExerciseStatus: CompletionStatus = 'planned';
  if (newSetStatuses.every((s) => s === 'skipped')) {
    newExerciseStatus = 'skipped';
  } else if (newSetStatuses.every((s) => s !== 'planned')) {
    newExerciseStatus = 'completed';
  }

  // Update exercise status if it needs to change
  if (exercise.status && exercise.status !== newExerciseStatus) {
    edits.push(
      ...editExerciseEdits(workoutLayer, exercise.workoutExerciseId, {
        ...exercise,
        status: newExerciseStatus,
      }),
    );
  }

  return edits;
}

/*
 * this function is used to change the status of the exercises and sets given the status of a workout
 * planned workouts should just update the status of the workout in the AthletePublishedWorkoutLayer
 * if the exercise is skipped, all sets that aren't completed should be skipped
 * if the exercise is completed, all sets that aren't skipped should be completed
 */
export function editsWorkoutStatus(
  actualLayer: HasWorkoutLayer | null | undefined,
  workout: Workout,
  status: 'completed' | 'skipped',
) {
  const edits: WorkoutLayerDomain.WorkoutLayerEdit[] = [];
  const athleteActualLayer =
    actualLayer?.layer ?? WorkoutLayerDomain.defaultLayer();

  workout.sections.forEach((section) => {
    section.entries.forEach((entry) => {
      const exercises = getExercisesFromEntry(entry, actualLayer ?? null);
      for (const exercise of exercises) {
        const editsForExercise: WorkoutLayerDomain.WorkoutLayerEdit[] = [];
        const currentExerciseStatus = exercise.status;

        exercise.sets.forEach((set) => {
          const currentSetStatus = set.status;
          if (!currentSetStatus || currentSetStatus === 'planned') {
            editsForExercise.push(
              ...editSetEdits(actualLayer ?? undefined, set.workoutSetId, {
                ...set,
                sortOrder: set.sortOrder,
                status,
              }),
            );
          }
        });

        const updatedActualLayer = WorkoutLayerDomain.mergeLayerEdits(
          athleteActualLayer,
          editsForExercise,
        );
        const isAllSkipped = Layer(updatedActualLayer)
          .setEdits({ workoutExerciseId: exercise.workoutExerciseId })
          .every((set) => set.status === 'skipped');

        if (!currentExerciseStatus || currentExerciseStatus === 'planned') {
          editsForExercise.push(
            ...editExerciseEdits(
              actualLayer ?? undefined,
              exercise.workoutExerciseId,
              {
                ...exercise,
                status: isAllSkipped ? 'skipped' : status,
              },
            ),
          );
        }

        edits.push(...editsForExercise);
      }
    });
  });

  return edits;
}
